import type {
  CashierOrder,
  ClientType,
  OrderPaymentMethod,
  OrderStatus,
  OrderType,
  PendingPaymentReturnState,
} from '@/types/trade'
import { isUnknownRecord } from '@/types/json'

/** 支付参数对象；第三方参数只用于内存中的支付跳转，不应写入日志或持久化。 */
export type PaymentParams = NonNullable<CashierOrder['pay_params']>

/** 支付回跳状态仅在当前标签页保留 24 小时。 */
const PAYMENT_RETURN_STATE_MAX_AGE = 24 * 60 * 60 * 1000
const PAYMENT_RETURN_STORAGE_PREFIX = 'secknow:alipay-return:'

/** 生成订单请求幂等键；仅用于请求关联，不包含用户或支付敏感信息。 */
export const createIdempotencyKey = (prefix = 'order'): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/** 生成订单对应的 sessionStorage 键。 */
const getPaymentReturnStorageKey = (orderNo: string): string =>
  `${PAYMENT_RETURN_STORAGE_PREFIX}${orderNo}`

/**
 * 获取本次支付应返回的当前页面。
 *
 * 生产支付只接受 HTTPS；本地 HTTP 开发环境不提交该字段，继续走后端兼容结果页。
 * 已有回跳参数会被移除，避免重复支付时嵌套旧订单状态。
 */
export const getFrontendPaymentReturnUrl = (): string | undefined => {
  if (typeof window === 'undefined' || window.location.protocol !== 'https:') return undefined
  const url = new URL(window.location.href)
  url.searchParams.delete('pay_result')
  url.searchParams.delete('order_no')
  return url.toString()
}

/** 保存当前标签页支付宝回跳所需的最小状态。 */
export const savePendingPaymentReturn = (order: CashierOrder): void => {
  if (typeof window === 'undefined') return
  const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const state: PendingPaymentReturnState = {
    order_no: order.order_no,
    return_path: returnPath.startsWith('/') && !returnPath.startsWith('//') ? returnPath : '/',
    create_time: Date.now(),
    ...(order.status_token === undefined ? {} : { status_token: order.status_token }),
  }
  try {
    window.sessionStorage.setItem(getPaymentReturnStorageKey(order.order_no), JSON.stringify(state))
  } catch {
    // 浏览器禁用存储时仍允许支付；回跳页会提示重新登录或回原页面确认。
  }
}

/** 读取并校验同标签页支付宝回跳状态。 */
export const loadPendingPaymentReturn = (orderNo: string): PendingPaymentReturnState | null => {
  if (typeof window === 'undefined' || !orderNo) return null
  try {
    const rawState = window.sessionStorage.getItem(getPaymentReturnStorageKey(orderNo))
    if (!rawState) return null
    const state: unknown = JSON.parse(rawState)
    if (!isUnknownRecord(state)) throw new Error('Invalid payment return state')

    const orderNumber = state['order_no']
    const returnPath = state['return_path']
    const createTime = state['create_time']
    const statusToken = state['status_token']
    const isValid =
      orderNumber === orderNo &&
      typeof returnPath === 'string' &&
      returnPath.startsWith('/') &&
      !returnPath.startsWith('//') &&
      typeof createTime === 'number' &&
      Number.isFinite(createTime) &&
      Date.now() - createTime <= PAYMENT_RETURN_STATE_MAX_AGE &&
      (statusToken === undefined || typeof statusToken === 'string')
    if (isValid) {
      return {
        order_no: orderNumber,
        return_path: returnPath,
        create_time: createTime,
        ...(statusToken === undefined ? {} : { status_token: statusToken }),
      }
    }
  } catch {
    // 非法或损坏的浏览器状态按不存在处理。
  }
  clearPendingPaymentReturn(orderNo)
  return null
}

/** 支付进入终态后清除回跳能力凭证。 */
export const clearPendingPaymentReturn = (orderNo: string): void => {
  if (typeof window === 'undefined' || !orderNo) return
  try {
    window.sessionStorage.removeItem(getPaymentReturnStorageKey(orderNo))
  } catch {
    // 清理失败不影响订单服务端状态。
  }
}

/** 能够进入收银台的订单最小字段集合。 */
export interface CashierOrderSource {
  order_no: string
  order_type: OrderType
  actual_paid: string
  payment_method: OrderPaymentMethod | null
  status?: OrderStatus
  pay_params?: PaymentParams
  status_token?: string
}

/** 手机端 UA 关键词；命中即视为手机浏览器。 */
const PHONE_UA_KEYWORDS = [
  'iphone',
  'ipod',
  'windows phone',
  'webos',
  'blackberry',
  'bb10',
  'mobile',
]

/** 平板 UA 关键词；命中即视为平板浏览器。 */
const TABLET_UA_KEYWORDS = ['ipad', 'tablet', 'playbook', 'silk', 'kindle']

/**
 * 判断当前浏览器适用的支付宝网页端类型。
 *
 * 手机与平板统一归为 wap（跳转支付宝收银台），仅桌面浏览器走 pc（扫码支付）。
 * 平板普遍安装支付宝 App，可直接唤起付款；若归为 pc 需要另一台设备扫码，体验更差。
 *
 * 判定顺序：
 * 1. 手机 UA 关键词
 * 2. Android：带 mobile 为手机，不带 mobile 为平板，两者都归 wap
 * 3. 平板 UA 关键词
 * 4. iPadOS 13+ 的 Safari 默认上报桌面版 Macintosh UA，不含 ipad 标识，
 *    需结合触控点数量识别；真实 macOS 设备的 maxTouchPoints 为 0
 *
 * @returns 支付端类型，pc=桌面扫码，wap=手机或平板跳转
 */
export const detectClientType = (): ClientType => {
  if (typeof navigator === 'undefined') return 'pc'
  const ua = navigator.userAgent.toLowerCase()
  if (PHONE_UA_KEYWORDS.some(keyword => ua.includes(keyword))) return 'wap'
  // Android 手机与 Android 平板都走跳转，无需再按 mobile 细分
  if (ua.includes('android')) return 'wap'
  if (TABLET_UA_KEYWORDS.some(keyword => ua.includes(keyword))) return 'wap'
  // iPadOS 13+ 桌面版 UA 兜底识别
  const touchPoints = typeof navigator.maxTouchPoints === 'number' ? navigator.maxTouchPoints : 0
  return ua.includes('macintosh') && touchPoints > 1 ? 'wap' : 'pc'
}

/** 判断支付跳转 URL 是否为支付宝官方网关。 */
export const isOfficialAlipayUrl = (value: unknown): value is string => {
  if (typeof value !== 'string' || !value) return false
  try {
    const url = new URL(value)
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      (!url.port || url.port === '443') &&
      url.pathname === '/gateway.do' &&
      (url.hostname === 'openapi.alipay.com' || url.hostname === 'openapi-sandbox.dl.alipaydev.com')
    )
  } catch {
    return false
  }
}

/**
 * 判断订单是否由真实支付宝网页网关承接。
 *
 * 电脑端走电脑网站支付跳转、手机端走手机网站支付跳转，两者都由后端返回
 * 官方网关 URL，因此校验方式一致。
 */
export const isRealAlipayOrder = (source: CashierOrderSource): boolean => {
  if (source.payment_method !== 'alipay') return false
  const params = source.pay_params
  if (!params || !('client_type' in params)) return false
  return isOfficialAlipayUrl(params.pay_url)
}

/** 将各业务订单响应转换成统一收银台模型，并保留支付参数与状态。 */
export const toCashierOrder = (
  source: CashierOrderSource,
  subject: string
): CashierOrder | null => {
  if (source.payment_method !== 'alipay' || source.status === 'paid') return null
  const params = source.pay_params
  if (!params) return null
  const payUrl = params.pay_url
  // 后端 Mock 支付订单带 is_mock 标记，允许走本地模拟收银台。
  const isMockOrder = 'is_mock' in params && params.is_mock
  // 支付宝订单只允许官方网关或后端 Mock；缺少真实参数且非 Mock 时禁止回退，避免免费结算真实订单。
  if (source.payment_method === 'alipay' && !isRealAlipayOrder(source) && !isMockOrder) return null
  const clientType = 'client_type' in params ? params.client_type : undefined
  return {
    order_no: source.order_no,
    order_type: source.order_type,
    subject,
    actual_paid: source.actual_paid,
    payment_method: source.payment_method,
    ...(source.status === undefined ? {} : { status: source.status }),
    ...(clientType === undefined ? {} : { client_type: clientType }),
    pay_params: params,
    ...(source.status_token === undefined ? {} : { status_token: source.status_token }),
    pay_url: payUrl,
    is_real_alipay: isRealAlipayOrder(source),
    can_cancel: source.status === 'pending' || source.status === undefined,
  }
}
